/**
 * Minimal HTTP client for the LBilling core JSON API, subscriber-portal edition.
 *
 * This is a trimmed sibling of apps/admin/src/shared/api/client.ts. It keeps the
 * SAME error envelope contract ({ "error": { code, message, details } } → thrown
 * as `ApiError` with `.code`/`.details`) so the typed captive client can consume
 * the exact backend contract, but it drops everything admin-specific: no
 * `credentials: 'include'` (the portal authenticates with a Bearer token, not the
 * staff session cookie), no global 401 → /login redirect, no error notifier.
 *
 * Base path `/api/v1` (the dev server proxies `/api` → http://localhost:8081, the
 * core's captive-portal listener; :8080 is the staff API and, since 2026-08-11,
 * serves no /api/v1/captive path at all — see vite.config.ts).
 */

export const API_BASE = '/api/v1';

export interface ApiErrorEnvelope {
  code: string;
  message: string;
  details?: unknown;
}

/** Normalised error thrown by every failed request. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  /** True when the request never reached the server (offline / DNS). */
  readonly isNetworkError: boolean;

  constructor(status: number, envelope: ApiErrorEnvelope, isNetworkError = false) {
    super(envelope.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope.code;
    this.details = envelope.details;
    this.isNetworkError = isNetworkError;
  }
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  try {
    const body = (await res.json()) as { error?: ApiErrorEnvelope };
    if (body && body.error && typeof body.error.message === 'string') {
      return body.error;
    }
  } catch {
    // response had no / malformed JSON body
  }
  return { code: `http_${res.status}`, message: `HTTP ${res.status}` };
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...rest,
    });
  } catch {
    throw new ApiError(0, { code: 'network_error', message: 'network_error' }, true);
  }

  if (!res.ok) {
    const envelope = await parseErrorEnvelope(res);
    throw new ApiError(res.status, envelope);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/** Thin verb helpers over apiFetch. */
export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
};
